import './authAccount.scss';

import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type FormEvent,
    type InputHTMLAttributes,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
} from 'react';
import { createRoot } from 'react-dom/client';

import { getAuthAccountRoute, type AuthAccountRoute } from './authAccountRoutes';
import { isCompanyAgnosticOAuthReturnUrl } from './authReturnUrl';

type AuthPageLabels = {
    login_id?: string;
    password?: string;
    customer_code: string;
    customer_missing?: string;
    remember_me: string;
    submit: string;
    forgot_credentials?: string;
};

type AuthFormPageData = {
    page: 'login' | 'sso-login';
    login_path: string;
    anti_forgery_token: string;
    login_id?: string;
    customer_code: string;
    remember_me: boolean;
    language_culture: string;
    message: string;
    labels: AuthPageLabels;
};

type TwoFactorPageData = {
    page: 'two-factor';
    login_path: string;
    anti_forgery_token: string;
    login_id: string;
    customer_code: string;
    two_factor_challenge_id: string;
    two_factor_method: string;
    two_factor_destination_hint: string;
    remember_me: boolean;
    language_culture: string;
    times_two_factor_has_expired: number;
    status_message: string;
    show_back_to_login: boolean;
    verify_path: string;
    resend_path: string;
};

type ForgotCredentialsPageData = {
    page: 'forgot-credentials';
    login_path: string;
    anti_forgery_token: string;
    user_id_or_email: string;
    customer_id: string;
    message: string;
    labels: {
        title: string;
        instruction: string;
        user_id_or_email: string;
        customer_id: string;
        submit: string;
        complete: string;
    };
};

type SessionTimeoutPageData = {
    page: 'session-timeout' | 'session-timeout-sso';
    login_path: string;
};

type PasswordManagementPageData = {
    login_path: string;
    anti_forgery_token: string;
    action_path: string;
    available: boolean;
    complete: boolean;
    screen_call_location: string;
    message: string;
};

type PasswordResetPageData = PasswordManagementPageData & {
    page: 'password-reset';
    hints: string[];
    labels: {
        title: string;
        instruction: string;
        current_password: string;
        new_password: string;
        confirm_password: string;
        submit: string;
        complete: string;
        close_tab: string;
    };
};

type PasswordHintPageData = PasswordManagementPageData & {
    page: 'password-hint';
    password_hint: string;
    labels: {
        title: string;
        instruction: string;
        password_hint: string;
        password_answer: string;
        submit: string;
        complete: string;
    };
};

type NavigableFormPageData = AuthFormPageData | ForgotCredentialsPageData;
type AuthPageData = NavigableFormPageData
    | TwoFactorPageData
    | PasswordResetPageData
    | PasswordHintPageData
    | SessionTimeoutPageData;

type AuthAccountAppProps = {
    initial_route: AuthAccountRoute;
    initial_page_data?: AuthPageData;
};

type AuthLayoutProps = {
    children: ReactNode;
};

type AuthInputProps = InputHTMLAttributes<HTMLInputElement> & {
    label: string;
    name: string;
};

type SubmitState = {
    busy: boolean;
    message: string;
};

type AuthFormDraft = {
    page: NavigableFormPageData['page'];
    user_identifier?: string;
    customer_code: string;
    remember_me?: boolean;
};

type AuthClientResponse = {
    message?: string;
    complete?: boolean;
    redirect_url?: string;
};

type NavigationState = {
    busy: boolean;
    message: string;
    target_href: string;
};

type AuthRouteLinkProps = {
    href: string;
    className: string;
    busy: boolean;
    onNavigate: (href: string, draft?: AuthFormDraft) => void;
    children: ReactNode;
};

const LOGO_PATH = '/Content/Images/Realinsight_Logo_OneLine_primary.svg';

function getAuthNavigationHref(pathname: string): string {
    const target_url = new URL(pathname, window.location.origin);
    const current_search_params = new URL(window.location.href).searchParams;
    const return_url = current_search_params.get('return_url');
    const return_customer_code = current_search_params.get('return_customer_code');
    if (return_url !== null) target_url.searchParams.set('return_url', return_url);
    if (return_customer_code !== null) target_url.searchParams.set('return_customer_code', return_customer_code);
    return target_url.pathname + target_url.search;
}

function readAuthFormDraft(form: HTMLFormElement | null): AuthFormDraft | undefined {
    if (form === null) return undefined;

    const page = form.dataset.authPage;
    if (page !== 'login' && page !== 'sso-login' && page !== 'forgot-credentials') return undefined;

    const form_data = new FormData(form);
    const user_identifier = page === 'forgot-credentials'
        ? form_data.get('UserIDorEMail')
        : form_data.get('LoginID');
    const customer_code = page === 'forgot-credentials'
        ? form_data.get('CustomerID')
        : form_data.get('CustomerCode');

    return {
        page,
        user_identifier: typeof user_identifier === 'string' ? user_identifier : undefined,
        customer_code: typeof customer_code === 'string' ? customer_code : '',
        remember_me: page === 'forgot-credentials'
            ? undefined
            : form_data.get('RememberMe') === 'true',
    };
}

function mergeAuthFormDraft(
    page_data: NavigableFormPageData,
    source_draft: AuthFormDraft | undefined,
    cached_draft: AuthFormDraft | undefined,
): NavigableFormPageData {
    if (page_data.page === 'forgot-credentials') {
        return {
            ...page_data,
            user_id_or_email: source_draft?.user_identifier
                ?? cached_draft?.user_identifier
                ?? page_data.user_id_or_email,
            customer_id: source_draft?.customer_code
                ?? cached_draft?.customer_code
                ?? page_data.customer_id,
        };
    }

    return {
        ...page_data,
        login_id: page_data.page === 'login'
            ? (source_draft?.user_identifier ?? cached_draft?.user_identifier ?? page_data.login_id)
            : page_data.login_id,
        customer_code: source_draft?.customer_code
            ?? cached_draft?.customer_code
            ?? page_data.customer_code,
        remember_me: source_draft?.remember_me
            ?? cached_draft?.remember_me
            ?? page_data.remember_me,
    };
}

function AuthLayout({ children }: AuthLayoutProps) {
    return (
        <div className="auth-account-stage">
            <section className="auth-account-card">
                <header className="auth-account-brand-header">
                    <img className="auth-account-logo" src={LOGO_PATH} alt="RealINSIGHT" />
                </header>
                <div className="auth-account-form-panel">
                    <div className="auth-account-form-content">{children}</div>
                </div>
            </section>
        </div>
    );
}

function AuthInput({ label, name, className = '', ...input_props }: AuthInputProps) {
    return (
        <label className="auth-account-field" htmlFor={name}>
            <span>{label}</span>
            <input
                {...input_props}
                className={`auth-account-input ${className}`.trim()}
                id={name}
                name={name}
            />
        </label>
    );
}

function EyeIcon({ hidden }: { hidden: boolean }) {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 12s3.6-5.2 9-5.2 9 5.2 9 5.2-3.6 5.2-9 5.2S3 12 3 12z" />
            <circle cx="12" cy="12" r="2.4" />
            {hidden ? <path d="M4 4l16 16" /> : null}
        </svg>
    );
}

function PasswordInput({
    label,
    name = 'Password',
    autoComplete = 'current-password',
    maskAsText = false,
}: {
    label: string;
    name?: string;
    autoComplete?: 'current-password' | 'new-password' | 'off';
    maskAsText?: boolean;
}) {
    const [show_password, set_show_password] = useState(false);
    const mask_text = maskAsText && !show_password;

    return (
        <label className="auth-account-field" htmlFor={name}>
            <span>{label}</span>
            <span className="auth-account-password-input">
                <input
                    className={`auth-account-input${mask_text ? ' auth-account-masked-text' : ''}`}
                    id={name}
                    name={name}
                    type={maskAsText || show_password ? 'text' : 'password'}
                    autoComplete={autoComplete}
                    required
                />
                <button
                    type="button"
                    className="auth-account-password-toggle"
                    aria-label={show_password ? 'Hide password' : 'Show password'}
                    aria-pressed={show_password}
                    onClick={() => set_show_password(current_value => !current_value)}
                >
                    <EyeIcon hidden={!show_password} />
                </button>
            </span>
        </label>
    );
}

function clearSensitiveInputs(form: HTMLFormElement): void {
    [
        'Password',
        'TwoFactorAnswer',
        'OldPasswordConfirmation',
        'NewPassword',
        'NewConfirmation',
        'PasswordAnswer',
    ].forEach(name => {
        const input = form.elements.namedItem(name);
        if (input instanceof HTMLInputElement) input.value = '';
    });
}

async function submitAuthForm(
    event: FormEvent<HTMLFormElement>,
    set_submit_state: (state: SubmitState) => void,
    handle_response?: (response: AuthClientResponse) => void,
): Promise<void> {
    event.preventDefault();

    const form = event.currentTarget;
    const native_event = event.nativeEvent as SubmitEvent;
    const submitter = native_event.submitter;
    const request_url = submitter instanceof HTMLButtonElement && submitter.hasAttribute('formaction')
        ? submitter.formAction
        : form.action;

    set_submit_state({ busy: true, message: '' });

    try {
        const response = await fetch(request_url, {
            method: 'POST',
            body: new FormData(form),
            headers: { 'X-Auth-Account-Client': 'true' },
            credentials: 'same-origin',
        });

        if (response.redirected) {
            window.location.assign(response.url);
            return;
        }

        const content_type = response.headers.get('Content-Type') ?? '';
        if (!content_type.includes('application/json')) {
            throw new Error('The authentication response was not JSON.');
        }

        const result = await response.json() as AuthClientResponse;
        if (typeof result.redirect_url === 'string' && result.redirect_url.length > 0) {
            const redirect_url = new URL(result.redirect_url, window.location.origin);
            if (redirect_url.origin !== window.location.origin) {
                throw new Error('The authentication redirect was not local.');
            }

            window.location.assign(redirect_url.href);
            return;
        }

        if (handle_response !== undefined) {
            handle_response(result);
        }
        set_submit_state({
            busy: false,
            message: result.message ?? (result.complete ? '' : 'Unable to continue.'),
        });
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to continue.';
        set_submit_state({ busy: false, message });
    }
    finally {
        clearSensitiveInputs(form);
    }
}

function FormMessage({ message }: { message: string }) {
    if (message.length === 0) return null;

    return (
        <div className="auth-account-form-error" role="alert">
            {message}
        </div>
    );
}

function RememberSettings({ checked, label }: { checked: boolean; label: string }) {
    return (
        <label className="auth-account-remember">
            <input name="RememberMe" type="checkbox" value="true" defaultChecked={checked} />
            <span>{label}</span>
        </label>
    );
}

function FormDivider() {
    return (
        <div className="auth-account-divider" aria-hidden="true">
            <span>or</span>
        </div>
    );
}

function AuthRouteLink({ href, className, busy, onNavigate, children }: AuthRouteLinkProps) {
    const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
        if (
            event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
        ) {
            return;
        }

        event.preventDefault();
        if (busy) return;

        onNavigate(href, readAuthFormDraft(event.currentTarget.closest('form')));
    };

    return (
        <a
            className={className}
            href={href}
            aria-disabled={busy}
            onClick={handleClick}
        >
            {children}
        </a>
    );
}

type AuthFormProps = {
    data: AuthFormPageData;
    navigation_state: NavigationState;
    onNavigate: (href: string, draft?: AuthFormDraft) => void;
};

function StandardLoginForm({ data, navigation_state, onNavigate }: AuthFormProps) {
    const [submit_state, set_submit_state] = useState<SubmitState>({
        busy: false,
        message: data.message,
    });
    const sso_href = getAuthNavigationHref('/SSO/login');

    return (
        <form
            className="auth-account-form"
            method="post"
            action={window.location.pathname + window.location.search}
            data-auth-page="login"
            onSubmit={event => { void submitAuthForm(event, set_submit_state); }}
        >
            <div className="auth-account-form-heading auth-account-visually-hidden">
                <h2>Sign in</h2>
                <p>Enter your account details to continue.</p>
            </div>
            <AuthInput
                label={data.labels.login_id ?? 'User ID'}
                name="LoginID"
                type="text"
                autoComplete="username"
                defaultValue={data.login_id ?? ''}
                autoFocus
                required
            />
            <PasswordInput label={data.labels.password ?? 'Password'} />
            <AuthInput
                label={data.labels.customer_code ?? 'Company ID'}
                name="CustomerCode"
                type="text"
                autoComplete="organization"
                defaultValue={data.customer_code ?? ''}
                required
            />
            <input name="LanguageCulture" type="hidden" value={data.language_culture ?? 'en-US'} />
            <input name="__RequestVerificationToken" type="hidden" value={data.anti_forgery_token} />
            <RememberSettings
                checked={data.remember_me}
                label={data.labels.remember_me ?? 'Remember my settings'}
            />
            <FormMessage message={submit_state.message} />
            <FormMessage message={navigation_state.message} />
            <button
                className="auth-account-button primary"
                type="submit"
                disabled={submit_state.busy || navigation_state.busy}
            >
                {submit_state.busy ? 'Signing in…' : (data.labels.submit ?? 'Sign in')}
            </button>
            <AuthRouteLink
                className="auth-account-text-link"
                href={getAuthNavigationHref('/RIBase/ForgotCredentials')}
                busy={navigation_state.busy}
                onNavigate={onNavigate}
            >
                {data.labels.forgot_credentials ?? 'Forgot User ID or Password?'}
            </AuthRouteLink>
            <FormDivider />
            <AuthRouteLink
                className="auth-account-button secondary"
                href={sso_href}
                busy={navigation_state.busy}
                onNavigate={onNavigate}
            >
                {navigation_state.busy && navigation_state.target_href === sso_href
                    ? 'Opening SSO…'
                    : 'Sign in with SSO'}
            </AuthRouteLink>
        </form>
    );
}

function SsoLoginForm({ data, navigation_state, onNavigate }: AuthFormProps) {
    const [submit_state, set_submit_state] = useState<SubmitState>({
        busy: false,
        message: data.message,
    });

    return (
        <form
            className="auth-account-form"
            method="post"
            action={window.location.pathname + window.location.search}
            data-auth-page="sso-login"
            onSubmit={event => { void submitAuthForm(event, set_submit_state); }}
        >
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Single sign-on</p>
                <h2>Sign in with SSO</h2>
                <p>Enter your Company ID to continue to your organization’s identity provider.</p>
            </div>
            <AuthInput
                label={data.labels.customer_code ?? 'Company ID'}
                name="CustomerCode"
                type="text"
                autoComplete="organization"
                defaultValue={data.customer_code ?? ''}
                autoFocus
                required
            />
            <input name="LanguageCulture" type="hidden" value={data.language_culture ?? 'en-US'} />
            <input name="__RequestVerificationToken" type="hidden" value={data.anti_forgery_token} />
            <RememberSettings
                checked={data.remember_me}
                label={data.labels.remember_me ?? 'Remember my settings'}
            />
            <FormMessage message={submit_state.message} />
            <FormMessage message={navigation_state.message} />
            <button
                className="auth-account-button primary"
                type="submit"
                disabled={submit_state.busy || navigation_state.busy}
            >
                {submit_state.busy ? 'Continuing…' : (data.labels.submit ?? 'Continue with SSO')}
            </button>
            <FormDivider />
            <AuthRouteLink
                className="auth-account-button secondary"
                href={getAuthNavigationHref(data.login_path)}
                busy={navigation_state.busy}
                onNavigate={onNavigate}
            >
                {navigation_state.busy ? 'Opening sign in…' : 'Back to standard sign in'}
            </AuthRouteLink>
        </form>
    );
}

function ForgotCredentialsForm({
    data,
    navigation_state,
    onNavigate,
}: {
    data: ForgotCredentialsPageData;
    navigation_state: NavigationState;
    onNavigate: (href: string, draft?: AuthFormDraft) => void;
}) {
    const [submit_state, set_submit_state] = useState<SubmitState>({
        busy: false,
        message: data.message,
    });
    const [complete, set_complete] = useState(false);

    if (complete) {
        return (
            <div className="auth-account-form auth-account-state-panel">
                <div className="auth-account-form-heading">
                    <p className="auth-account-eyebrow">Request received</p>
                    <h2>Check your email</h2>
                    <p>{data.labels.complete}</p>
                </div>
                <AuthRouteLink
                    className="auth-account-button primary"
                    href={getAuthNavigationHref(data.login_path)}
                    busy={navigation_state.busy}
                    onNavigate={onNavigate}
                >
                    Return to sign in
                </AuthRouteLink>
            </div>
        );
    }

    return (
        <form
            className="auth-account-form"
            method="post"
            action={window.location.pathname + window.location.search}
            data-auth-page="forgot-credentials"
            onSubmit={event => {
                const form = event.currentTarget;
                void submitAuthForm(event, set_submit_state, response => {
                    if (response.complete === true) {
                        form.reset();
                        set_complete(true);
                    }
                });
            }}
        >
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Account recovery</p>
                <h2>{data.labels.title}</h2>
                <p>{data.labels.instruction}</p>
            </div>
            <AuthInput
                label={data.labels.user_id_or_email}
                name="UserIDorEMail"
                type="text"
                autoComplete="username"
                defaultValue={data.user_id_or_email ?? ''}
                autoFocus
                required
            />
            <AuthInput
                label={data.labels.customer_id}
                name="CustomerID"
                type="text"
                autoComplete="organization"
                defaultValue={data.customer_id ?? ''}
                required
            />
            <input name="__RequestVerificationToken" type="hidden" value={data.anti_forgery_token} />
            <FormMessage message={submit_state.message} />
            <FormMessage message={navigation_state.message} />
            <button
                className="auth-account-button primary"
                type="submit"
                disabled={submit_state.busy || navigation_state.busy}
            >
                {submit_state.busy ? 'Submitting…' : data.labels.submit}
            </button>
            <AuthRouteLink
                className="auth-account-text-link"
                href={getAuthNavigationHref(data.login_path)}
                busy={navigation_state.busy}
                onNavigate={onNavigate}
            >
                Back to sign in
            </AuthRouteLink>
        </form>
    );
}

function PasswordUnavailable() {
    return (
        <div className="auth-account-form auth-account-state-panel">
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Unavailable</p>
                <h2>Password management unavailable</h2>
                <p>System is not configured with RealINSIGHT Authentication.</p>
            </div>
            <a className="auth-account-button primary" href="/">Return home</a>
        </div>
    );
}

function PasswordResetForm({ data }: { data: PasswordResetPageData }) {
    const [submit_state, set_submit_state] = useState<SubmitState>({
        busy: false,
        message: data.message,
    });
    const [complete, set_complete] = useState(data.complete);
    const [form_ready, set_form_ready] = useState(false);

    if (!data.available) return <PasswordUnavailable />;

    if (complete) {
        return (
            <div className="auth-account-form auth-account-state-panel">
                <div className="auth-account-form-heading">
                    <p className="auth-account-eyebrow">Password updated</p>
                    <h2>{data.labels.complete}</h2>
                    {data.labels.close_tab.length > 0 ? <p>{data.labels.close_tab}</p> : null}
                </div>
            </div>
        );
    }

    const update_form_ready = (form: HTMLFormElement) => {
        const current_password = form.elements.namedItem('OldPasswordConfirmation');
        const new_password = form.elements.namedItem('NewPassword');
        const confirmation = form.elements.namedItem('NewConfirmation');
        set_form_ready(
            current_password instanceof HTMLInputElement
            && new_password instanceof HTMLInputElement
            && confirmation instanceof HTMLInputElement
            && current_password.value.length > 0
            && new_password.value.length > 0
            && new_password.value === confirmation.value,
        );
    };

    return (
        <form
            className="auth-account-form"
            method="post"
            action={data.action_path}
            onInput={event => update_form_ready(event.currentTarget)}
            onSubmit={event => {
                void submitAuthForm(event, set_submit_state, response => {
                    if (response.complete === true) set_complete(true);
                    set_form_ready(false);
                });
            }}
        >
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Account security</p>
                <h2>{data.labels.title}</h2>
                {data.labels.instruction.length > 0 ? <p>{data.labels.instruction}</p> : null}
            </div>
            <PasswordInput label={data.labels.current_password} name="OldPasswordConfirmation" />
            <PasswordInput label={data.labels.new_password} name="NewPassword" autoComplete="new-password" />
            <PasswordInput label={data.labels.confirm_password} name="NewConfirmation" autoComplete="new-password" />
            <input name="ScreenCallLocation" type="hidden" value={data.screen_call_location ?? ''} />
            <input name="__RequestVerificationToken" type="hidden" value={data.anti_forgery_token} />
            <FormMessage message={submit_state.message} />
            <button
                className="auth-account-button primary"
                type="submit"
                disabled={submit_state.busy || !form_ready}
            >
                {submit_state.busy ? 'Updating…' : data.labels.submit}
            </button>
            {data.hints.length > 0 ? (
                <ul className="auth-account-policy">
                    {data.hints.map(hint => <li key={hint}>{hint}</li>)}
                </ul>
            ) : null}
        </form>
    );
}

function PasswordHintForm({ data }: { data: PasswordHintPageData }) {
    const [submit_state, set_submit_state] = useState<SubmitState>({
        busy: false,
        message: data.message,
    });
    const [complete, set_complete] = useState(data.complete);

    if (!data.available) return <PasswordUnavailable />;

    if (complete) {
        return (
            <div className="auth-account-form auth-account-state-panel">
                <div className="auth-account-form-heading">
                    <p className="auth-account-eyebrow">Security answer updated</p>
                    <h2>{data.labels.complete}</h2>
                </div>
                <a className="auth-account-button primary" href="/">Go Home</a>
            </div>
        );
    }

    return (
        <form
            className="auth-account-form"
            method="post"
            action={data.action_path}
            onSubmit={event => {
                void submitAuthForm(event, set_submit_state, response => {
                    if (response.complete === true) set_complete(true);
                });
            }}
        >
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Account security</p>
                <h2>{data.labels.title}</h2>
                {data.labels.instruction.length > 0 ? <p>{data.labels.instruction}</p> : null}
            </div>
            <AuthInput
                label={data.labels.password_hint}
                name="PasswordHint"
                type="text"
                defaultValue={data.password_hint ?? ''}
                autoFocus
                required
            />
            <PasswordInput
                label={data.labels.password_answer}
                name="PasswordAnswer"
                autoComplete="off"
                maskAsText
            />
            <input name="ScreenCallLocation" type="hidden" value={data.screen_call_location ?? ''} />
            <input name="__RequestVerificationToken" type="hidden" value={data.anti_forgery_token} />
            <FormMessage message={submit_state.message} />
            <button className="auth-account-button primary" type="submit" disabled={submit_state.busy}>
                {submit_state.busy ? 'Updating…' : data.labels.submit}
            </button>
        </form>
    );
}

function TwoFactorForm({ data }: { data: TwoFactorPageData }) {
    const [submit_state, set_submit_state] = useState<SubmitState>({
        busy: false,
        message: data.status_message,
    });
    const method_label = data.two_factor_method.toLowerCase() === 'email' ? 'email' : 'phone';
    const delivery_message = data.show_back_to_login
        ? 'This verification attempt can no longer be used. Return to login to request a new code.'
        : data.two_factor_destination_hint.length === 0
            ? 'Enter the verification code to continue.'
            : `Enter the verification code sent to ${data.two_factor_destination_hint}.`;

    if (data.show_back_to_login) {
        return (
            <div className="auth-account-form auth-account-state-panel">
                <div className="auth-account-form-heading">
                    <p className="auth-account-eyebrow">Verification required</p>
                    <h2>Secure your sign-in</h2>
                    <p>{delivery_message}</p>
                </div>
                <div className="auth-account-method">Verification method: <strong>{method_label}</strong></div>
                <FormMessage message={data.status_message} />
                <a className="auth-account-button primary" href={data.login_path}>Back to Login</a>
            </div>
        );
    }

    return (
        <form
            className="auth-account-form"
            method="post"
            action={data.verify_path}
            autoComplete="off"
            onSubmit={event => { void submitAuthForm(event, set_submit_state); }}
        >
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Verification required</p>
                <h2>Secure your sign-in</h2>
                <p>{delivery_message}</p>
            </div>
            <div className="auth-account-method">Verification method: <strong>{method_label}</strong></div>
            <AuthInput
                label="Verification Code"
                name="TwoFactorAnswer"
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit code"
                className="auth-account-code-input"
                autoFocus
                required
            />
            <input name="LoginID" type="hidden" value={data.login_id} />
            <input name="CustomerCode" type="hidden" value={data.customer_code} />
            <input name="TwoFactorChallengeId" type="hidden" value={data.two_factor_challenge_id} />
            <input name="TwoFactorMethod" type="hidden" value={data.two_factor_method} />
            <input name="TwoFactorDestinationHint" type="hidden" value={data.two_factor_destination_hint} />
            <input name="LanguageCulture" type="hidden" value={data.language_culture} />
            <input name="ClientCulture" type="hidden" value={data.language_culture} />
            <input name="RememberMe" type="hidden" value={data.remember_me ? 'true' : 'false'} />
            <input name="TimesTwoFactorHasExpired" type="hidden" value={data.times_two_factor_has_expired} />
            <input name="__RequestVerificationToken" type="hidden" value={data.anti_forgery_token} />
            <FormMessage message={submit_state.message} />
            <div className="auth-account-button-row">
                <button className="auth-account-button primary" type="submit" disabled={submit_state.busy}>
                    Verify Code
                </button>
                <button
                    className="auth-account-button secondary"
                    type="submit"
                    formAction={data.resend_path}
                    formNoValidate
                    disabled={submit_state.busy}
                >
                    Resend Code
                </button>
            </div>
            <p className="auth-account-form-note">
                If the code does not arrive, request a new one. Only the most recent code will work.
            </p>
        </form>
    );
}

function SessionTimeout({ login_path }: { login_path: string }) {
    return (
        <div className="auth-account-form auth-account-state-panel">
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Session ended</p>
                <h2>Your session has timed out</h2>
                <p>For your security, please sign in again to continue.</p>
            </div>
            <a className="auth-account-button primary" href={login_path}>Return to sign in</a>
        </div>
    );
}

function UnsupportedRoute() {
    return (
        <div className="auth-account-form auth-account-state-panel">
            <div className="auth-account-form-heading">
                <p className="auth-account-eyebrow">Unavailable</p>
                <h2>Authentication page unavailable</h2>
                <p>This authentication route is not configured.</p>
            </div>
            <a className="auth-account-button primary" href="/RIBase/LogIn">Return to sign in</a>
        </div>
    );
}

function isNavigableFormPageData(page_data: AuthPageData): page_data is NavigableFormPageData {
    return page_data.page === 'login'
        || page_data.page === 'sso-login'
        || page_data.page === 'forgot-credentials';
}

function toAuthFormDraft(page_data: NavigableFormPageData): AuthFormDraft {
    if (page_data.page === 'forgot-credentials') {
        return {
            page: page_data.page,
            user_identifier: page_data.user_id_or_email,
            customer_code: page_data.customer_id,
        };
    }

    return {
        page: page_data.page,
        user_identifier: page_data.login_id,
        customer_code: page_data.customer_code,
        remember_me: page_data.remember_me,
    };
}

function AuthAccountApp({ initial_route, initial_page_data }: AuthAccountAppProps) {
    const [view, set_view] = useState({
        route: initial_route,
        page_data: initial_page_data,
    });
    const [navigation_state, set_navigation_state] = useState<NavigationState>({
        busy: false,
        message: '',
        target_href: '',
    });
    const form_drafts = useRef<Partial<Record<NavigableFormPageData['page'], AuthFormDraft>>>(
        initial_page_data !== undefined && isNavigableFormPageData(initial_page_data)
            ? { [initial_page_data.page]: toAuthFormDraft(initial_page_data) }
            : {},
    );
    const navigation_request = useRef(0);

    const navigateToForm = useCallback(async (
        href: string,
        source_draft: AuthFormDraft | undefined,
        history_mode: 'push' | 'none',
    ) => {
        const target_url = new URL(href, window.location.origin);
        const target_route = getAuthAccountRoute(target_url.pathname);
        if (target_route?.kind !== 'form') {
            window.location.assign(target_url.href);
            return;
        }

        if (source_draft !== undefined) {
            form_drafts.current[source_draft.page] = source_draft;
        }

        const request = ++navigation_request.current;
        set_navigation_state({
            busy: true,
            message: '',
            target_href: target_url.pathname + target_url.search,
        });

        try {
            const loaded_page_data = await loadAuthPageData(target_url);
            if (!isNavigableFormPageData(loaded_page_data) || loaded_page_data.page !== target_route.id) {
                throw new Error('The requested authentication form was not returned.');
            }

            if (request !== navigation_request.current) return;

            const page_data = mergeAuthFormDraft(
                loaded_page_data,
                source_draft,
                form_drafts.current[loaded_page_data.page],
            );
            form_drafts.current[page_data.page] = toAuthFormDraft(page_data);

            if (history_mode === 'push') {
                window.history.pushState(null, '', target_url);
            }

            document.title = target_route.title;
            set_view({ route: target_route, page_data });
            set_navigation_state({ busy: false, message: '', target_href: '' });
        }
        catch (error) {
            if (request !== navigation_request.current) return;

            if (history_mode === 'none') {
                window.location.reload();
                return;
            }

            const message = error instanceof Error
                ? error.message
                : 'Unable to open the authentication form.';
            set_navigation_state({ busy: false, message, target_href: '' });
        }
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            const target_url = new URL(window.location.href);
            const target_route = getAuthAccountRoute(target_url.pathname);
            if (target_route?.kind !== 'form') {
                window.location.reload();
                return;
            }

            const active_form = document.querySelector<HTMLFormElement>('form[data-auth-page]');
            void navigateToForm(target_url.href, readAuthFormDraft(active_form), 'none');
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [navigateToForm]);

    const handleFormNavigation = (href: string, draft?: AuthFormDraft) => {
        void navigateToForm(href, draft, 'push');
    };

    let content: ReactNode;

    if (view.route.kind === 'session-timeout') {
        const login_path = view.page_data?.page === 'session-timeout'
            || view.page_data?.page === 'session-timeout-sso'
            ? view.page_data.login_path
            : view.route.login_path ?? '/RIBase/LogIn';
        content = <SessionTimeout login_path={login_path} />;
    }
    else if (view.route.kind === 'two-factor' && view.page_data?.page === 'two-factor') {
        content = <TwoFactorForm data={view.page_data} />;
    }
    else if (view.route.kind === 'form' && view.page_data?.page === 'login') {
        content = (
            <StandardLoginForm
                data={view.page_data}
                navigation_state={navigation_state}
                onNavigate={handleFormNavigation}
            />
        );
    }
    else if (view.route.kind === 'form' && view.page_data?.page === 'sso-login') {
        content = (
            <SsoLoginForm
                data={view.page_data}
                navigation_state={navigation_state}
                onNavigate={handleFormNavigation}
            />
        );
    }
    else if (view.route.kind === 'form' && view.page_data?.page === 'forgot-credentials') {
        content = (
            <ForgotCredentialsForm
                data={view.page_data}
                navigation_state={navigation_state}
                onNavigate={handleFormNavigation}
            />
        );
    }
    else if (view.route.kind === 'password-management' && view.page_data?.page === 'password-reset') {
        content = <PasswordResetForm data={view.page_data} />;
    }
    else if (view.route.kind === 'password-management' && view.page_data?.page === 'password-hint') {
        content = <PasswordHintForm data={view.page_data} />;
    }
    else {
        content = <UnsupportedRoute />;
    }

    return (
        <AuthLayout>
            <div className="auth-account-form-view" key={view.route.id}>
                {content}
            </div>
        </AuthLayout>
    );
}

async function loadAuthPageData(page_url = new URL(window.location.href)): Promise<AuthPageData> {
    const bootstrap_url = new URL(page_url);
    bootstrap_url.searchParams.set('auth_page_data', 'true');
    const response = await fetch(bootstrap_url, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
    });
    if (response.redirected) {
        window.location.assign(response.url);
        throw new Error('Authentication is required to continue.');
    }
    if (!response.ok) throw new Error(`Authentication page data failed with status ${response.status}.`);
    return await response.json() as AuthPageData;
}

function bindReturnUrlToStoredCustomerCode(page_url: URL): URL {
    if (!page_url.searchParams.has('return_url') || page_url.searchParams.has('return_customer_code')) {
        return page_url;
    }

    const return_url = page_url.searchParams.get('return_url') ?? '';
    if (isCompanyAgnosticOAuthReturnUrl(return_url)) return page_url;

    const customer_code = window.localStorage.getItem('RICustomerCode')?.trim() ?? '';
    if (!customer_code) return page_url;

    const bound_page_url = new URL(page_url);
    bound_page_url.searchParams.set('return_customer_code', customer_code);
    window.history.replaceState(window.history.state, '', bound_page_url.pathname + bound_page_url.search);
    return bound_page_url;
}

function renderApp(root: HTMLElement, route: AuthAccountRoute, page_data?: AuthPageData): void {
    createRoot(root).render(
        <AuthAccountApp initial_route={route} initial_page_data={page_data} />,
    );
}

async function startAuthAccountApp(): Promise<void> {
    const root = document.getElementById('auth-account-root');
    if (root === null) throw new Error('Auth/account root element was not found.');

    const route = getAuthAccountRoute(window.location.pathname);
    if (route === undefined) {
        createRoot(root).render(
            <AuthLayout>
                <UnsupportedRoute />
            </AuthLayout>,
        );
        return;
    }

    const page_url = bindReturnUrlToStoredCustomerCode(new URL(window.location.href));

    if (route.clear_storage) window.localStorage.clear();

    document.title = route.title;
    try {
        renderApp(root, route, await loadAuthPageData(page_url));
    }
    catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load authentication page.';
        createRoot(root).render(
            <AuthLayout>
                <div className="auth-account-form auth-account-state-panel">
                    <div className="auth-account-form-heading">
                        <p className="auth-account-eyebrow">Unable to continue</p>
                        <h2>Authentication page unavailable</h2>
                        <p>{message}</p>
                    </div>
                    <a className="auth-account-button primary" href="/RIBase/LogIn">Return to sign in</a>
                </div>
            </AuthLayout>,
        );
    }
}

void startAuthAccountApp();
